import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, cleanHandle } from "@/lib/api";
import { Avatar, Confirm, Panel, PrimaryButton, Title, ago } from "@/components/admin/kit";
import { utc } from "@/components/admin/MansionsPanel";

const DECREES = { docks_day: "Docks Day", fast_tide: "Fast Tide", fireworks: "Fireworks", tax_holiday: "Tax Holiday", lucky_cargo: "Lucky Cargo", duel_night: "Duel Night" };
const ballots = (n) => `${n} ballot${n === 1 ? "" : "s"}`;

function Countdown({ to }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const s = Math.max(0, Math.floor((new Date(to) - now) / 1000));
  return (
    <span className="tabular-nums" data-testid="election-countdown">
      {s >= 86400 ? `${Math.floor(s / 86400)}d ` : ""}{Math.floor((s % 86400) / 3600)}h {Math.floor((s % 3600) / 60)}m {String(s % 60).padStart(2, "0")}s
    </span>
  );
}

const Face = ({ p, size = 32 }) => (
  <>
    <Avatar src={p.a} alt={`@${p.h}`} size={size} />
    <a href={`/?at=${p.h}`} className="font-semibold hover:underline">@{p.h}</a>
  </>
);

export function ElectionsPanel() {
  const [d, setD] = useState(null);
  const [h, setH] = useState("");
  useEffect(() => { api("/admin/election").then(setD).catch((e) => toast.error(e.message)); }, []);
  if (!d) return <div className="text-slate-500">Loading…</div>;
  const act = (path, method, body, done) => api(path, { method, body }).then((x) => { setD(x); toast.success(done); }).catch((e) => toast.error(e.message));
  const mayor = (body, done) => act("/admin/mayor", "PUT", body, done);
  const handle = cleanHandle(h);
  return (
    <div className="grid gap-5 lg:grid-cols-2" data-testid="elections-panel">
      <div className="space-y-5">
        <Panel testId="election-mayor-panel">
          <Title hint="Whoever wins the weekly election holds the office until the next count.">Mayor</Title>
          {d.mayor ? (
            <div className="flex items-start gap-3" data-testid="election-mayor">
              <Avatar src={d.mayor.a} alt={`@${d.mayor.h}`} size={56} />
              <div className="min-w-0">
                <a href={`/?at=${d.mayor.h}`} className="font-display text-lg font-bold hover:underline">@{d.mayor.h}</a>
                {d.mayor.slogan && <p className="text-sm text-slate-600">“{d.mayor.slogan}”</p>}
                <p className="text-xs text-slate-500">Took office {ago(d.mayor.since)}</p>
              </div>
            </div>
          ) : <div className="text-sm text-slate-500" data-testid="election-no-mayor">There is no mayor right now.</div>}
          <dl className="mt-4 space-y-2 text-sm">
            <div className="flex items-center gap-2" data-testid="election-decree">
              <dt className="w-16 shrink-0 font-semibold">Decree</dt>
              <dd className="min-w-0 flex-1 truncate">{d.decree ? `${DECREES[d.decree.kind] || d.decree.kind}, until ${utc(d.decree.until)}` : <span className="text-slate-400">none</span>}</dd>
              {d.decree && <PrimaryButton tone="soft" className="px-2.5 py-1.5" onClick={() => mayor({ clear_decree: true }, "Decree cleared")} testId="election-clear-decree">Clear decree</PrimaryButton>}
            </div>
            <div className="flex items-center gap-2" data-testid="election-pin">
              <dt className="w-16 shrink-0 font-semibold">Pin</dt>
              <dd className="min-w-0 flex-1 truncate">{d.pin ? <>@{d.pin.h}: {d.pin.text}</> : <span className="text-slate-400">none</span>}</dd>
              {d.pin && <PrimaryButton tone="soft" className="px-2.5 py-1.5" onClick={() => mayor({ clear_pin: true }, "Pin cleared")} testId="election-clear-pin">Clear pin</PrimaryButton>}
            </div>
          </dl>
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
            <Input value={h} onChange={(e) => setH(e.target.value)} placeholder="@handle" className="h-9 w-44" data-testid="election-mayor-input" />
            <Confirm tone="soft" title={`Make @${handle} the mayor?`} action="Set mayor" disabled={!handle} testId="election-set-mayor"
              text="They take office right now, with no slogan. The current mayor steps down and any decree or pin goes with them."
              onConfirm={() => mayor({ h: handle }, `@${handle} is the mayor`)}>Set mayor</Confirm>
            <Confirm title="Clear the mayor?" action="Clear mayor" disabled={!d.mayor} testId="election-clear-mayor"
              text={`@${d.mayor?.h} steps down and the office stays empty until the next count.`}
              onConfirm={() => mayor({ h: null }, "Mayor cleared")}>Clear mayor</Confirm>
          </div>
        </Panel>
        <Panel testId="election-past-panel">
          <Title>Past mayors</Title>
          {d.past.length ? (
            <ul className="space-y-2 text-sm" data-testid="election-past">
              {d.past.map((p) => (
                <li key={`${p.week}-${p.k}`} className="flex items-center gap-2.5">
                  <span className="w-24 shrink-0 text-slate-400">{p.week}</span>
                  <Face p={p} size={28} />
                  <span className="ml-auto shrink-0 tabular-nums text-slate-500">{ballots(p.ballots)}</span>
                </li>
              ))}
            </ul>
          ) : <div className="text-sm text-slate-500">Nobody has held the office yet.</div>}
        </Panel>
      </div>
      <Panel testId="election-race-panel">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <Title hint={d.closes_at ? `Closes ${utc(d.closes_at)}` : "No count is scheduled."}>
            The race{d.closes_at && <span className="ml-2 font-body text-sm font-semibold text-slate-500">closes in <Countdown to={d.closes_at} /></span>}
          </Title>
          <Confirm tone="brand" title="Count the ballots now?" action="Count now" testId="election-count"
            text="The ballots are counted right away and the winner becomes mayor, without waiting for the countdown."
            onConfirm={() => act("/admin/election/count", "POST", undefined, "Ballots counted")}>Count now</Confirm>
        </div>
        {d.race.length ? (
          <ol data-testid="election-race">
            {d.race.map((c, i) => (
              <li key={c.k} className="flex items-center gap-3 border-t border-slate-100 py-2.5 first:border-0" data-testid={`election-candidate-${c.k}`}>
                <span className="w-6 shrink-0 font-display text-lg font-bold tabular-nums text-slate-400">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2"><Face p={c} size={28} /></div>
                  {c.slogan && <p className="truncate text-sm text-slate-600">“{c.slogan}”</p>}
                </div>
                <span className="shrink-0 text-sm tabular-nums text-slate-600">{ballots(c.ballots)}</span>
                <Confirm title={`Remove @${c.h} from the race?`} action="Remove candidate" className="px-2.5 py-1.5" testId={`election-remove-${c.k}`}
                  text={`@${c.h} is out and their candidacy fee is refunded.`}
                  onConfirm={() => act(`/admin/election/remove/${c.h}`, "POST", undefined, `@${c.h} removed, fee refunded`)}>Remove</Confirm>
              </li>
            ))}
          </ol>
        ) : <div className="text-sm text-slate-500" data-testid="election-empty">Nobody is running this week.</div>}
      </Panel>
    </div>
  );
}
